import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, AlertTriangle, XCircle, 
  ShoppingBag, Slack, RefreshCw, ExternalLink,
  Link2, Clock, LogIn
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Integration Status Widget
 * 
 * Displays the health of integrations with intentional UI states:
 * - not_connected: Show "Connect Shopify" CTA (Shopify not connected - normal state)
 * - connected: Show healthy status with stats
 * - syncing: Show sync in progress
 * - needs_attention: Show reconnect CTA
 * - no_data: Show waiting for data
 * - needs_auth: Show "Session expired" (only when session is actually expired)
 * - error: Show retry with support link (backend/network error)
 * 
 * CRITICAL: 
 * - Uses credentials: "include" for cookie-based auth
 * - Distinguishes between auth failure (401) vs network error vs real "not connected"
 * - Never shows "Sign In Required" when user is actually logged in
 */
export default function IntegrationStatusWidget({ className = '' }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    const token = sessionStorage.getItem('session_token');
    
    // If no token in sessionStorage, show "Connect Shopify" (not auth error)
    // This handles the case where user is logged in via cookies but token isn't in sessionStorage
    if (!token) {
      // Try the fetch anyway with credentials - cookies might handle auth
      try {
        const response = await fetch(`${API_URL}/api/integration/status`, {
          method: 'GET',
          credentials: 'include', // CRITICAL: Send cookies
          headers: { 
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setStatus(data);
        } else if (response.status === 401) {
          // Only show auth error if both token AND cookies fail
          setStatus({
            ok: false,
            state: 'not_connected',
            message: 'Connect Shopify to unlock Margin Guard, benchmarks, and weekly digests.',
            cta_text: 'Connect Shopify',
            cta_link: '/dashboard/settings/integrations'
          });
        } else {
          // Server error - show "Connect Shopify" as safe fallback
          setStatus({
            ok: false,
            state: 'not_connected',
            message: 'Connect Shopify to unlock Margin Guard, benchmarks, and weekly digests.',
            cta_text: 'Connect Shopify',
            cta_link: '/dashboard/settings/integrations'
          });
        }
      } catch (err) {
        // Network error - show "Connect Shopify" as safe fallback
        setStatus({
          ok: false,
          state: 'not_connected',
          message: 'Connect Shopify to unlock Promise Health, benchmarks, and weekly digests.',
          cta_text: 'Connect Shopify',
          cta_link: '/dashboard/settings/integrations'
        });
      }
      setLoading(false);
      return;
    }

    // Token exists - make authenticated request
    try {
      const response = await fetch(`${API_URL}/api/integration/status`, {
        method: 'GET',
        credentials: 'include', // CRITICAL: Send cookies
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Backend returned needs_auth but we have a token - treat as "not_connected"
        // This handles the case where backend session doesn't match frontend token
        if (data.state === 'needs_auth') {
          setStatus({
            ...data,
            state: 'not_connected',
            message: 'Connect Shopify to unlock Margin Guard, benchmarks, and weekly digests.',
            cta_text: 'Connect Shopify',
            cta_link: '/dashboard/settings/integrations'
          });
        } else {
          setStatus(data);
        }
      } else if (response.status === 401) {
        // 401 with a token means session expired - but show graceful fallback
        setStatus({
          ok: false,
          state: 'not_connected',
          message: 'Connect Shopify to unlock Promise Health, benchmarks, and weekly digests.',
          cta_text: 'Connect Shopify',
          cta_link: '/dashboard/settings/integrations'
        });
      } else {
        // Other server error
        setStatus({
          ok: false,
          state: 'error',
          message: 'Unable to check integration status.',
          cta_text: 'Retry',
          cta_link: null
        });
      }
    } catch (err) {
      // Network error - show graceful fallback
      console.error('Integration status fetch error:', err);
      setStatus({
        ok: false,
        state: 'not_connected',
        message: 'Connect Shopify to unlock Promise Health, benchmarks, and weekly digests.',
        cta_text: 'Connect Shopify',
        cta_link: '/dashboard/settings/integrations',
        _networkError: true
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={`bg-[#0E1426]/80 border-[#22304A] ${className}`}>
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-[#22304A] rounded w-3/4"></div>
            <div className="h-4 bg-[#22304A] rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // State configurations for UI rendering
  const stateConfig = {
    connected: {
      icon: CheckCircle,
      iconColor: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      badgeLabel: 'Connected',
      badgeColor: 'bg-green-500/20 text-green-400 border-green-500/30'
    },
    not_connected: {
      icon: Link2,
      iconColor: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      badgeLabel: 'Setup Required',
      badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    },
    syncing: {
      icon: RefreshCw,
      iconColor: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      badgeLabel: 'Syncing',
      badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    },
    needs_attention: {
      icon: AlertTriangle,
      iconColor: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      badgeLabel: 'Needs Attention',
      badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    },
    no_data: {
      icon: Clock,
      iconColor: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      badgeLabel: 'Waiting for Data',
      badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    },
    needs_auth: {
      icon: LogIn,
      iconColor: 'text-gray-400',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/30',
      badgeLabel: 'Session Expired',
      badgeColor: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    },
    error: {
      icon: XCircle,
      iconColor: 'text-gray-400',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/30',
      badgeLabel: 'Unavailable',
      badgeColor: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  };

  const config = stateConfig[status?.state] || stateConfig.not_connected;
  const StateIcon = config.icon;

  return (
    <Card 
      className={`bg-[#0E1426]/80 border-[#22304A] ${className}`} 
      data-testid="integration-status-widget"
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
              <StateIcon className={`w-4 h-4 ${config.iconColor} ${status?.state === 'syncing' ? 'animate-spin' : ''}`} />
            </div>
            <span className="text-white text-sm font-medium">Integration Status</span>
          </div>
          <Badge 
            variant="outline" 
            className={`text-xs ${config.badgeColor}`}
          >
            {config.badgeLabel}
          </Badge>
        </div>

        {/* Message */}
        <p className="text-[#9CB0C7] text-sm mb-3 leading-relaxed">
          {status?.message || 'Connect Shopify to unlock Margin Guard and weekly digests.'}
        </p>

        {/* Stats (only show when connected) */}
        {status?.state === 'connected' && status?.shopify?.connected && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-[#0A1628] rounded-lg p-2">
              <div className="text-xs text-[#9CB0C7] mb-0.5">Orders</div>
              <div className="text-white text-sm font-medium">
                {(status?.shopify?.orders_observed || 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-[#0A1628] rounded-lg p-2">
              <div className="text-xs text-[#9CB0C7] mb-0.5">Products</div>
              <div className="text-white text-sm font-medium">
                {(status?.shopify?.products_synced || 0).toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {/* Waiting for data progress indicator */}
        {status?.state === 'no_data' && (
          <div className="bg-[#0A1628] rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#9CB0C7]">Sync Progress</span>
              <span className="text-xs text-blue-400">Processing...</span>
            </div>
            <div className="w-full bg-[#22304A] rounded-full h-1.5">
              <div className="bg-blue-500 h-1.5 rounded-full animate-pulse" style={{width: '30%'}}></div>
            </div>
            <p className="text-xs text-[#9CB0C7] mt-2">
              Orders will appear as customers place them. First data typically arrives within a few hours.
            </p>
          </div>
        )}

        {/* Integration icons */}
        {(status?.state === 'connected' || status?.state === 'no_data') && (
          <div className="flex items-center gap-3 mb-3">
            {/* Shopify */}
            <div className="flex items-center gap-1.5">
              <ShoppingBag className={`w-4 h-4 ${status?.shopify?.connected ? 'text-green-400' : 'text-gray-500'}`} />
              <span className={`text-xs ${status?.shopify?.connected ? 'text-green-400' : 'text-gray-500'}`}>
                Shopify {status?.shopify?.connected ? '✓' : ''}
              </span>
            </div>
            {/* Slack */}
            <div className="flex items-center gap-1.5">
              <Slack className={`w-4 h-4 ${status?.slack?.connected ? 'text-green-400' : 'text-gray-500'}`} />
              <span className={`text-xs ${status?.slack?.connected ? 'text-green-400' : 'text-gray-500'}`}>
                Slack {status?.slack?.connected ? '✓' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Slack CTA when Shopify connected but Slack not */}
        {(status?.state === 'connected' || status?.state === 'no_data') && 
         status?.shopify?.connected && 
         !status?.slack?.connected && (
          <div className="bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Slack className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-sm font-medium text-white">Get alerts in Slack</span>
            </div>
            <p className="text-xs text-[#9CB0C7] mb-2">
              Receive weekly briefs and approve decisions without leaving Slack.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard/settings/slack')}
              className="w-full bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white text-xs"
              data-testid="connect-slack-cta"
            >
              Connect Slack
            </Button>
          </div>
        )}

        {/* CTA Button */}
        {status?.cta_text && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (status.cta_link) {
                navigate(status.cta_link);
              } else if (status.cta_text === 'Retry') {
                loadStatus();
              }
            }}
            className={`w-full text-sm ${
              status.state === 'not_connected' || status.state === 'needs_attention'
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'
            }`}
            data-testid="integration-status-cta"
          >
            {status.cta_text}
            {status.cta_link && <ExternalLink className="w-3 h-3 ml-2" />}
          </Button>
        )}

        {/* Retry link for error states */}
        {(status?.state === 'error' || status?._networkError) && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <button 
              onClick={loadStatus}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Retry
            </button>
            <span className="text-gray-600">•</span>
            <a 
              href="mailto:support@commerceindex.com"
              className="text-xs text-gray-500 hover:text-gray-400"
            >
              Contact support
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact Integration Status for nav/header
 * Shows just the status dot and label
 * 
 * CRITICAL: Properly distinguishes between:
 * - "Not connected" (Shopify not set up - normal)
 * - "Status unavailable" (network/auth error - neutral)
 * - "Connected" (all good)
 */
export function IntegrationStatusBadge({ className = '' }) {
  const [status, setStatus] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    loadStatus();
    // Refresh every 5 minutes
    const interval = setInterval(loadStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    const token = sessionStorage.getItem('session_token');
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/integration/status`, {
        method: 'GET',
        credentials: 'include', // CRITICAL: Send cookies
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setFetchFailed(false);
        
        // Map needs_auth to a neutral state for the header badge
        if (data.state === 'needs_auth') {
          setStatus({ ...data, state: 'not_connected' });
        } else {
          setStatus(data);
        }
      } else {
        // API error - show neutral state, NOT "not connected"
        setFetchFailed(true);
        setStatus({ state: 'unavailable' });
      }
    } catch (err) {
      // Network error - show neutral state
      setFetchFailed(true);
      setStatus({ state: 'unavailable' });
    }
  };

  // Don't show anything while loading
  if (!status) return null;

  const config = {
    connected: {
      dotColor: 'bg-green-500',
      textColor: 'text-green-400',
      label: 'Connected'
    },
    not_connected: {
      dotColor: 'bg-blue-500',
      textColor: 'text-blue-400',
      label: 'Setup Required'
    },
    syncing: {
      dotColor: 'bg-blue-500',
      textColor: 'text-blue-400',
      label: 'Syncing'
    },
    needs_attention: {
      dotColor: 'bg-yellow-500',
      textColor: 'text-yellow-400',
      label: 'Needs Attention'
    },
    no_data: {
      dotColor: 'bg-blue-500',
      textColor: 'text-blue-400',
      label: 'Waiting for Data'
    },
    unavailable: {
      dotColor: 'bg-gray-500',
      textColor: 'text-gray-400',
      label: 'Status Unavailable'
    },
    error: {
      dotColor: 'bg-gray-500',
      textColor: 'text-gray-400',
      label: 'Status Unavailable'
    },
    needs_auth: {
      dotColor: 'bg-gray-500',
      textColor: 'text-gray-400',
      label: 'Status Unavailable'
    }
  };

  const style = config[status.state] || config.not_connected;

  return (
    <span 
      className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-black/20 ${className}`}
      title={`Integration: ${style.label}`}
      data-testid="integration-status-badge"
    >
      <span className={`w-2 h-2 rounded-full ${style.dotColor} ${status.state === 'syncing' ? 'animate-pulse' : ''}`}></span>
      <span className={style.textColor}>{style.label}</span>
    </span>
  );
}
